import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { PanelDeCelebraciones, ProximosEventos, TopReconocimientos } from '@/components/PanelesDeEquipo';
import { Skeleton } from '@/ui/Skeleton';
import { Compositor } from './Compositor';
import { Filtros } from './Filtros';
import { Publicacion } from './Publicacion';
import {
  FILTROS_VACIOS, type Filtros as EstadoDeFiltros, hayFiltro, type Publicacion as Datos, useMuro,
} from './api';

/**
 * Novedades — el muro del portal.
 *
 * ## Qué es esta pantalla
 *
 * Lo que está pasando en la organización, en orden y sin fin: se escribe arriba
 * y se lee hacia abajo. Desde el 2026-09-07 es el **único** sitio donde vive el
 * muro — Inicio enseña tres titulares y trae aquí.
 *
 * ## Lo que cambió al migrarla (2026-09-08)
 *
 * Siete defectos que no daban error, medidos sobre `page-feed.js`:
 *
 *   1. **Diez sitios metían texto de una persona crudo en el HTML.** El peor:
 *      `author_name`, que al firmar a nombre del área NO es el nombre del
 *      directorio sino `nombre_publico`, 120 caracteres de texto libre que
 *      escribe quien publica. `test_escapado.py` estaba en verde y no los veía
 *      porque su lista casa `\.name`, no `author_name` — el mismo hueco que ya
 *      tuvieron `filename` y `location`. Aquí todo es texto de React.
 *   2. **La barra lateral pedía `/kudos/leaderboard` sin comprobar el módulo.**
 *      Un 403 que se pintaba como «No se pudo cargar». Ver `PanelesDeEquipo`.
 *   3. **Seis peticiones al entrar, dos evitables.** `/users/me/profile` solo
 *      para la foto que el shell ya tenía, y la lista de palabras vetadas antes
 *      de que nadie fuera a escribir. Quedan cuatro.
 *   4. **La insignia «Jefe» daba 2,50 de contraste** (3,62 en oscuro). Tinte y
 *      tinta del mismo tono, el defecto de las insignias de Solicitudes.
 *   5. **Cinco clases CSS sin un solo consumidor.**
 *   6. **Dos `onmouseover` como atributo HTML** en el selector de autor, sin
 *      equivalente de teclado.
 *   7. **134 estilos en línea, 28 `onclick`, 23 hexadecimales y 17 `rgba()`.**
 *
 * ## La tarjeta no es solo de esta pantalla
 *
 * `Publicacion` la monta también Comunidades, por
 * `window.PortalReact.montarPublicaciones`. Antes iba por
 * `PageFeed.renderPostHTML`; migrar sin resolverlo habría dejado dos tarjetas
 * divergiendo.
 */
export function Novedades() {
  const [filtros, setFiltros] = useState<EstadoDeFiltros>(FILTROS_VACIOS);
  // El texto escrito se separa del que viaja: sin esto, cada tecla lanzaba una
  // consulta. 350 ms es lo que ya usaba la vanilla.
  const [busquedaEscrita, setBusquedaEscrita] = useState('');

  useEffect(() => {
    const t = setTimeout(
      () => setFiltros((f) => (f.busqueda === busquedaEscrita ? f : { ...f, busqueda: busquedaEscrita })),
      350,
    );
    return () => clearTimeout(t);
  }, [busquedaEscrita]);

  const muro = useMuro(filtros);
  const cliente = useQueryClient();
  const refrescar = () => void cliente.invalidateQueries({ queryKey: ['novedades', 'muro'] });

  const publicaciones = useMemo(() => muro.data?.pages.flat() ?? [], [muro.data]);
  const filtrado = hayFiltro(filtros);

  return (
    <div className="flex flex-col gap-5">
      <header>
        <h1 className="flex items-center gap-2 text-7 font-extrabold text-brand-ink">
          <i className="bi bi-newspaper" aria-hidden="true" /> Novedades
        </h1>
        <p className="mt-1 text-3 text-muted">Lo que está pasando con tu equipo</p>
      </header>

      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        {/* ── El muro ─────────────────────────────────────────────── */}
        <div className="flex min-w-0 flex-col gap-4">
          <Compositor alPublicar={refrescar} />

          <Filtros
            valor={{ ...filtros, busqueda: busquedaEscrita }}
            onCambio={(f) => {
              setBusquedaEscrita(f.busqueda);
              // Todo menos la búsqueda se aplica en el acto: son elecciones,
              // no escritura, y esperar 350 ms a una píldora se nota.
              setFiltros((actual) => ({ ...f, busqueda: actual.busqueda }));
            }}
          />

          {muro.isPending ? (
            <div className="flex flex-col gap-4" role="status" aria-live="polite">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-44 rounded-md" />
              ))}
              <span className="sr-only">Cargando publicaciones…</span>
            </div>
          ) : muro.isError ? (
            <EstadoError
              err={muro.error}
              que="el muro"
              onReintentar={() => void muro.refetch()}
            />
          ) : !publicaciones.length ? (
            filtrado ? (
              <EstadoVacio
                icono="bi-funnel"
                titulo="Ninguna publicación coincide con el filtro"
                descripcion="Prueba a quitar algún filtro o a ampliar el período."
                accion={{
                  texto: 'Quitar filtros',
                  onClick: () => {
                    setBusquedaEscrita('');
                    setFiltros(FILTROS_VACIOS);
                  },
                }}
              />
            ) : (
              <EstadoVacio
                icono="bi-chat-square"
                titulo="Sin publicaciones"
                descripcion="Sé el primero en compartir algo con el equipo."
              />
            )
          ) : (
            <ListaDelMuro publicaciones={publicaciones} alCambiar={refrescar} />
          )}

          {/* El centinela del desplazamiento infinito. Va fuera del bloque de
              arriba para que no se desmonte al cambiar de estado. */}
          <Centinela
            hayMas={muro.hasNextPage}
            cargando={muro.isFetchingNextPage}
            onPedirMas={() => void muro.fetchNextPage()}
          />
        </div>

        {/* ── Lo del equipo ───────────────────────────────────────── */}
        <aside className="flex flex-col gap-5 lg:sticky lg:top-20">
          <PanelDeCelebraciones />
          <ProximosEventos />
          <TopReconocimientos habilitado={Auth.hasModule('kudos')} />
        </aside>
      </div>
    </div>
  );
}

/* ── La lista, con sus separadores de fecha ────────────────────────────── */

/**
 * Las publicaciones agrupadas por «Hoy», «Ayer», «Esta semana»…
 *
 * El grupo se calcula por publicación y se compara con el anterior, así que un
 * separador aparece solo cuando el grupo cambia de verdad. En la versión vanilla
 * el último grupo vivía en una variable de módulo (`_lastGroup`) y sobrevivía a
 * los cambios de filtro: pulsar un filtro después de bajar podía dejar el muro
 * sin el primer separador.
 */
function ListaDelMuro({
  publicaciones, alCambiar,
}: {
  publicaciones: Datos[];
  alCambiar: () => void;
}) {
  let grupoAnterior: string | null = null;
  return (
    <div className="flex flex-col gap-4">
      {publicaciones.map((p) => {
        const grupo = grupoDeFecha(p.created_at);
        const nuevo = grupo !== grupoAnterior;
        grupoAnterior = grupo;
        return (
          <div key={p.id} className="contents">
            {nuevo && (
              <p className="flex items-center gap-3 text-2 font-semibold uppercase tracking-wide text-muted">
                <span className="h-px flex-1 bg-line" aria-hidden="true" />
                {grupo}
                <span className="h-px flex-1 bg-line" aria-hidden="true" />
              </p>
            )}
            <Publicacion publicacion={p} alCambiar={alCambiar} />
          </div>
        );
      })}
    </div>
  );
}

/** «Hoy», «Ayer», «Esta semana», «Este mes» o el mes y el año. */
export function grupoDeFecha(fecha: string) {
  const d = new Date(fecha);
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const dia = new Date(d);
  dia.setHours(0, 0, 0, 0);
  const diferencia = Math.round((hoy.getTime() - dia.getTime()) / 86_400_000);
  if (diferencia <= 0) return 'Hoy';
  if (diferencia === 1) return 'Ayer';
  if (diferencia < 7) return 'Esta semana';
  if (diferencia < 30) return 'Este mes';
  return d.toLocaleDateString('es-CO', { month: 'long', year: 'numeric' });
}

/* ── Desplazamiento infinito ───────────────────────────────────────────── */

/**
 * Pide la página siguiente al acercarse el final, y deja un botón para quien no
 * puede provocar ese acercamiento.
 *
 * El botón no es un adorno: un `IntersectionObserver` a secas deja sin salida a
 * quien navega con teclado —tabular no desplaza hasta el centinela si no hay
 * nada enfocable— y a los lectores de pantalla, que recorren el árbol sin mover
 * la ventana. Con `rootMargin` de 300 px la carga empieza antes de que el hueco
 * se vea, así que en uso normal el botón nunca llega a pulsarse.
 */
function Centinela({
  hayMas, cargando, onPedirMas,
}: {
  hayMas: boolean;
  cargando: boolean;
  onPedirMas: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  // El callback vive en un ref y NO en las dependencias del efecto.
  //
  // `onPedirMas` llega como `() => void muro.fetchNextPage()`, una función
  // nueva en cada render. Con ella en las dependencias, el efecto desconecta y
  // vuelve a crear el observador en CADA render del muro —y el muro re-renderiza
  // cada vez que alguien reacciona o abre un hilo—. Como
  // `IntersectionObserver` notifica de forma asíncrona tras `observe()`, un
  // aviso que llegue justo después de un `disconnect()` se pierde.
  //
  // Con el ref, el observador se crea una vez por cambio real de estado
  // (`hayMas` / `cargando`) y el callback que ejecuta siempre es el último.
  const pedirMas = useRef(onPedirMas);
  pedirMas.current = onPedirMas;

  useEffect(() => {
    const el = ref.current;
    if (!el || !hayMas || cargando) return;
    const observador = new IntersectionObserver(
      (entradas) => {
        if (entradas[0]?.isIntersecting) pedirMas.current();
      },
      { rootMargin: '300px' },
    );
    observador.observe(el);
    return () => observador.disconnect();
  }, [hayMas, cargando]);

  if (!hayMas) return null;

  return (
    <div ref={ref} className="flex justify-center py-2">
      {cargando ? (
        <p className="text-2 text-muted" role="status" aria-live="polite">
          Cargando más publicaciones…
        </p>
      ) : (
        <button
          type="button"
          onClick={onPedirMas}
          className="rounded-sm px-4 py-2 text-2 font-semibold text-brand-ink underline
                     underline-offset-2 focus-visible:focus-anillo pointer-coarse:min-h-11"
        >
          Ver más publicaciones
        </button>
      )}
    </div>
  );
}
